/**
 * HiringTeam — invite, list, role-edit, and remove team members for the recruiter org.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import {
  Users, Plus, Mail, Shield, Trash2, ArrowLeft, Crown, Loader2, X, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const ROLE_CHIP = {
  owner:     "tb-chip-butter",
  admin:     "tb-chip-lavender",
  recruiter: "tb-chip-mint",
  viewer:    "tb-chip-sky",
};

const ROLES = ["admin", "recruiter", "viewer"];

export default function HiringTeam() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [invitingOpen, setInvitingOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("recruiter");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/marketplace/hiring/team").then((r) => setData(r.data)).catch(() => setData({ org: null, members: [] }));

  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      await api.post("/marketplace/hiring/team/invite", { email: email.trim(), role });
      toast.success(`Invite sent to ${email}`);
      setEmail("");
      setInvitingOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not invite");
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (m, newRole) => {
    try {
      await api.post(`/marketplace/hiring/team/${m.member_id}/role`, { role: newRole });
      toast.success(`${m.name || m.email} is now ${newRole}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from the hiring team?`)) return;
    try {
      await api.post(`/marketplace/hiring/team/${m.member_id}/remove`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <Layout title="Hiring Team">
      <Button variant="ghost" size="sm" onClick={() => navigate("/hiring")} className="rounded-full mb-3" data-testid="team-back">
        <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to hiring hub
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
            <Users className="h-3 w-3" /> Institution · Team
          </span>
          <h1 className="mt-3 font-display text-3xl tracking-tight font-medium leading-tight">Your hiring team</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Invite recruiters, set roles, and manage who can post jobs and view candidates.</p>
        </div>
        <Button className="rounded-full" onClick={() => setInvitingOpen(true)} data-testid="team-invite-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Invite member
        </Button>
      </div>

      {!data ? (
        <Skeleton className="h-72 rounded-3xl" />
      ) : !data.org ? (
        <div className="tb-card p-10 text-center" data-testid="team-empty">
          <Users className="h-6 w-6 text-muted-foreground inline-block mb-2" />
          <p className="font-display text-lg font-medium">Set up your hiring org first</p>
          <Button className="rounded-full mt-3" onClick={() => navigate("/hiring")}>Open Hiring Hub</Button>
        </div>
      ) : (
        <div className="tb-card overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/40 grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">
            <div className="col-span-5">Member</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {data.members.map((m) => (
            <div key={m.member_id} className="px-5 py-3 border-b border-border last:border-b-0 grid grid-cols-12 gap-2 items-center hover:bg-muted/30 transition" data-testid={`team-row-${m.member_id}`}>
              <div className="col-span-5 flex items-center gap-2.5 min-w-0">
                <span className="h-9 w-9 rounded-2xl tb-chip-lavender grid place-items-center font-display text-sm font-medium shrink-0">
                  {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{m.name || "Pending teacher"}</p>
                  <p className="text-[11px] text-muted-foreground">{m.role === "owner" ? "Org owner" : "Team member"}</p>
                </div>
              </div>
              <div className="col-span-3 text-xs text-muted-foreground truncate">{m.email}</div>
              <div className="col-span-2">
                {m.role === "owner" ? (
                  <span className={`${ROLE_CHIP.owner} rounded-full text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide inline-flex items-center gap-1`}>
                    <Crown className="h-2.5 w-2.5" /> Owner
                  </span>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" className="rounded-full text-[11px] h-7 px-2.5" data-testid={`team-role-${m.member_id}`}>
                        <span className={`${ROLE_CHIP[m.role] || "tb-chip-sky"} rounded-full text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide mr-1`}>{m.role}</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {ROLES.filter((r) => r !== m.role).map((r) => (
                        <DropdownMenuItem key={r} onClick={() => changeRole(m, r)} data-testid={`team-role-${m.member_id}-${r}`}>
                          Set as {r}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <div className="col-span-2 text-right flex items-center justify-end gap-2">
                <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${m.status === "active" ? "tb-chip-mint" : "tb-chip-butter"}`}>{m.status}</span>
                {m.role !== "owner" && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => remove(m)} data-testid={`team-remove-${m.member_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {invitingOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-3" onClick={() => setInvitingOpen(false)} data-testid="team-invite-modal">
          <div className="bg-card rounded-3xl border border-border max-w-md w-full p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="font-display text-lg font-medium tracking-tight inline-flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Invite to hiring team
                </p>
                <p className="text-[12px] text-muted-foreground mt-0.5">They'll receive a notification on next sign-in.</p>
              </div>
              <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setInvitingOpen(false)}><X className="h-4 w-4" /></Button>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Email</span>
              <input className="tb-input mt-1" placeholder="recruiter@school.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="team-invite-email" />
            </label>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Role</span>
              <select className="tb-input mt-1" value={role} onChange={(e) => setRole(e.target.value)} data-testid="team-invite-role">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">
                {role === "admin"     && "Can invite, post jobs, view candidates."}
                {role === "recruiter" && "Can post jobs and view candidates."}
                {role === "viewer"    && "Read-only — sees jobs and stats."}
              </p>
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setInvitingOpen(false)}>Cancel</Button>
              <Button className="rounded-full" onClick={invite} disabled={busy} data-testid="team-invite-submit">
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
                Send invite
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
